/**
 * 图例颜色配置工具函数
 */
import { ChartType, type TChartType } from '@dbl/core/chart';
import { LIGHT_COLOR_ARR, LIGHT_RGBA_COLORS } from '@customComp/Chart/_config/_constant';
import { hsvaToHex, hsvaToRgbaString, rgbaStringToHsva, hexToHsva } from '@uiw/color-convert';
import type { TLegendColors } from './types';

interface ISeriesItem {
  name?: string;
  metric?: string;
  control_name_cn?: string;
  control_name_en?: string;
  [key: string]: unknown;
}

interface IBarLikeChartData {
  seriesData?: ISeriesItem[];
}

interface IHorizontalBarChartDataItem {
  series?: ISeriesItem[];
}

interface IPieChartDimensionItem {
  dimension_name?: string;
  [key: string]: unknown;
}

interface IMixedChartSeriesContainer {
  series_data?: ISeriesItem[];
}

interface IChartData {
  barChart?: IBarLikeChartData;
  horizontalBarData?: IHorizontalBarChartDataItem[];
  percentStackBarData?: IBarLikeChartData;
  percentLineChartData?: IBarLikeChartData;
  stackBarChart?: IBarLikeChartData;
  lineChart?: IBarLikeChartData;
  eventLineChart?: IBarLikeChartData;
  pieChartData?: Array<Record<string, IPieChartDimensionItem[]>>;
  mixedChartData?: IMixedChartSeriesContainer;
  [key: string]: unknown;
}

const CHART_TYPES_WITH_LEGEND: TChartType[] = [
  ChartType.Pie,
  ChartType.Line,
  ChartType.EventLine,
  ChartType.Bar,
  ChartType.HorizontalBarChart,
  ChartType.StackBar,
  ChartType.PercentStackBar,
  ChartType.PercentLine,
  ChartType.MixedChart,
];

/**
 * 从 chartData 中按图表类型提取图例信息（有序）。
 * - 颜色 key：统一使用中文 controlName + 展示名，通过 buildLegendKey 生成（与 seriesUtils 保持一致）；
 * - 展示文案：根据 isEn 选择中/英文 controlName，再与展示名拼接。
 */
export function getLegendInfoFromChartData(
  chartType: TChartType,
  chartData: IChartData | undefined,
  isEn: boolean,
): { legendItems: { key: string; label: string }[]; controlName?: string } {
  if (!chartData) return { legendItems: [] };
  if (!CHART_TYPES_WITH_LEGEND.includes(chartType)) return { legendItems: [] };

  const {
    barChart,
    horizontalBarData,
    percentStackBarData,
    percentLineChartData,
    stackBarChart,
    lineChart,
    eventLineChart,
    pieChartData,
    mixedChartData,
  } = chartData;
  // 柱状图、条形图、堆叠图、百分比堆叠/折线：seriesData 或 series
  const seriesData =
    barChart?.seriesData ||
    horizontalBarData?.[0]?.series ||
    percentStackBarData?.seriesData ||
    percentLineChartData?.seriesData ||
    stackBarChart?.seriesData ||
    lineChart?.seriesData ||
    eventLineChart?.seriesData ||
    mixedChartData?.series_data;

  if (Array.isArray(seriesData)) {
    return buildLegendInfoFromSeries(seriesData, isEn);
  }

  // 饼图：pieChartData 结构为 [ { [metric]: [{ dimension_name }] } ]
  const pieData = pieChartData;
  if (Array.isArray(pieData) && pieData.length > 0) {
    const legendItems: { key: string; label: string }[] = [];
    const seen = new Set<string>();
    pieData.forEach((item) => {
      Object.keys(item).forEach((metricKey) => {
        const arr = item[metricKey];
        if (Array.isArray(arr)) {
          arr.forEach((d) => {
            const name = (d?.dimension_name ?? '') as string;
            if (name && !seen.has(name)) {
              seen.add(name);
              const label = String(name);
              legendItems.push({ key: label, label });
            }
          });
        }
      });
    });
    return { legendItems };
  }

  return { legendItems: [] };
}

/**
 * 按 index 获取默认颜色（hexa + alpha）
 */
export function getDefaultColorInfo(index: number): { hexa: string; alpha: string } {
  const hex = LIGHT_COLOR_ARR[index % LIGHT_COLOR_ARR.length] ?? '#5086F3';
  return { hexa: hex, alpha: '100' };
}

/**
 * 在 draft 中按 name 查找颜色（rgba），无则返回默认 rgba（来自 LIGHT_RGBA_COLORS）。
 */
export function findRgbaForLegend(
  name: string,
  index: number,
  draftColors: TLegendColors | undefined,
): string {
  const found = draftColors?.[name];
  if (found) return found;
  return LIGHT_RGBA_COLORS[index % LIGHT_RGBA_COLORS.length] ?? 'rgba(80, 134, 243, 1)';
}

/**
 * 统一的 legendKey 生成规则（与图表渲染侧保持一致）。
 * - 有对照组：`${controlName},${seriesDataName}`
 * - 无对照组：`${seriesDataName}`
 */
export function buildLegendKey(params: {
  controlName: string;
  seriesDataName: string;
}): string {
  const { controlName, seriesDataName } = params;
  if (controlName) {
    return `${controlName},${seriesDataName || ''}`;
  }
  return seriesDataName || '';
}

/**
 * 将 legendKey 的有序列表转换成稳定的 paletteIndex 映射（取首次出现的下标）。
 * 这样在“对比时间重复 series/去重展示”等场景下，两端默认色索引一致且不跳色。
 */
export function buildLegendPaletteIndexMap(
  legendKeysInOrder: string[],
): Record<string, number> {
  const map: Record<string, number> = {};
  legendKeysInOrder.forEach((key, index) => {
    if (!key) return;
    if (!Number.isFinite(map[key])) {
      map[key] = index;
    }
  });
  return map;
}

/**
 * 设置 rgba/rgb 字符串的透明度（alpha）。
 */
export function setRgbaAlpha(rgba: string, alpha: number): string {
  const nextAlpha = clampNum(alpha, 0, 1);
  const input = String(rgba || '');
  if (input.startsWith('rgba(')) {
    return input.replace(/[\d.]+(?=\))/, () => nextAlpha.toString());
  }
  if (input.startsWith('rgb(')) {
    return input.replace(/^rgb\(/, 'rgba(').replace(/\)$/, `, ${nextAlpha})`);
  }
  return input;
}

/**
 * rgba 字符串 -> ColorPalette 的 colorInfo（hexa + alpha%）。
 */
export function rgbaToColorInfo(rgba: string): { hexa: string; alpha: string } {
  try {
    const hsva = rgbaStringToHsva(rgba);
    const hexa = hsvaToHex(hsva);
    const alpha = String(Math.round(clampNum(hsva.a, 0, 1) * 100));
    return { hexa, alpha };
  } catch (_) {
    return { hexa: '#5086F3', alpha: '100' };
  }
}

/**
 * ColorPalette 的 colorInfo（hexa + alpha%）-> rgba 字符串
 */
export function colorInfoToRgba(info: { hexa: string; alpha: string }): string {
  const hsva = hexToHsva(info.hexa);
  const alphaNum = clampNum(Number(info.alpha) / 100, 0, 1);
  return hsvaToRgbaString({ ...hsva, a: alphaNum });
}

/**
 * 支持的图表类型
 */
export { CHART_TYPES_WITH_LEGEND };

function buildLegendInfoFromSeries(
  series: ISeriesItem[],
  isEn: boolean,
): { legendItems: { key: string; label: string }[]; controlName?: string } {
  const legendItems: { key: string; label: string }[] = [];
  const seen = new Set<string>();

  const controlNameDisplaySet = new Set<string>();

  series.forEach((item) => {
    const {
      name,
      metric,
      control_name_cn: controlNameCnRaw,
      control_name_en: controlNameEnRaw,
    } = item ?? {};
    const displayNameBase = name || metric || '';
    if (!displayNameBase) return;

    const controlNameCn = String(controlNameCnRaw ?? '');
    const controlNameEn = String(controlNameEnRaw ?? '');

    const key = buildLegendKey({
      controlName: controlNameCn,
      seriesDataName: displayNameBase,
    });
    if (!key) return;

    const controlNameDisplay = isEn ? controlNameEn : controlNameCn;
    const label = controlNameDisplay
      ? `${controlNameDisplay},${displayNameBase}`
      : displayNameBase;

    if (!seen.has(key)) {
      seen.add(key);
      legendItems.push({ key, label });
    }

    if (controlNameDisplay) {
      controlNameDisplaySet.add(controlNameDisplay);
    }
  });

  if (controlNameDisplaySet.size === 1) {
    const onlyName = Array.from(controlNameDisplaySet)[0];
    if (onlyName) return { legendItems, controlName: onlyName };
  }

  return { legendItems };
}

function clampNum(v: number, min: number, max: number): number {
  const n = Number.isFinite(v) ? v : min;
  return Math.min(max, Math.max(min, n));
}

